import {A360ProductModel} from 'Quoting/models/Quoting';

export type AttributeValue =
  | {valueType: 'String'; stringValue: string}
  | {valueType: 'Integer'; integerValue: number}
  | {valueType: 'Datetime'; datetimeValue: string};

type CommonAttributeType = {
  id: string;
  entityName: string;
  attributeName: string;
};

interface Attribute extends CommonAttributeType {
  _type: string;
  attributeValue: {_type: 'Value'} & AttributeValue;
}

interface Dimension extends CommonAttributeType {
  attributeValue: AttributeValue;
}

export interface RetrievePlansWithCoverageRequestPayload {
  attributes: Attribute[];
  dimensions: Dimension[];
  rateEffectiveDate: string;
  rateRequestDate: string;
  _type: string;
}

export interface RetrievePlansRequestData {
  productModel: A360ProductModel;
  sicCode: string;
  numberOfEligibleLives: number;
  riskStateCode: string;
  termEffectiveDate: string;
  rateEffectiveDate: string;
  rateRequestDate: string;
}

export type AttributeBehavior = 'AttributeDefaultBehavior' | 'AttributeResetBehavior' | 'AttributeMinMaxBehavior';

export type RetrievePlanConfigurationRequestPayload = Partial<{
  planCd: string;
  productModel: A360ProductModel;
  situsStateCd: string;
  packageCd: string;
  numberOfEligibleLives: number;
  rateEffectiveDate: string;
  rateRequestDate: string;
  uwOffer: string;
  coverageCodes: string[];
}>;

export interface RatingError {
  errorCode: string;
  message: string;
  errors?: RatingError[];
  logReference: string;
  details?: ErrorDetails;
}

export interface ErrorDetails {
  ruleName: string;
  entityName: string;
  entityId: string;
  fieldName: string;
}

export interface AttributeFilter {
  _type: 'Value';
  entityName: string;
  attributeName: string;
}

export const CompensationProcessingType = {
  IndividualHeapedPercent: 'IndividualHeapedPercent',
  FlatPercent: 'FlatPercent',
} as const;

export type ProcessingType =
  | typeof CompensationProcessingType.FlatPercent
  | typeof CompensationProcessingType.IndividualHeapedPercent;

interface CompensationFlatPercentRule {
  processingType: typeof CompensationProcessingType.FlatPercent;
  compensationRate: {
    rate: number;
    denomination: string;
  };
}
interface CompensationIndividualHeapedRule {
  processingType: typeof CompensationProcessingType.IndividualHeapedPercent;
  bounds: {
    lowerBound: number;
    upperBound: number;
    rate: number;
    denomination: string;
  }[];
}

export interface PredefinedHierarchicalContracts {
  contractResolutionResponse: {
    premiumSplitParticipant: {
      producerLevel: number;
    };
    applicabilityPeriod: {
      start: string;
    };
    contracts: {
      applicableParameters: {
        applicabilityPeriod: {};
        productCode: string;
      };
      processingDefinition: {
        calculationDefinition: {
          rule: CompensationFlatPercentRule | CompensationIndividualHeapedRule;
          compensationType: string;
        };
      };
      contractName: string;
    }[];
  }[];
}

export interface PredefinedHierarchicalContractsRequestPayload {
  premiumSequence: {
    policyInfo: {
      commissionStyle: string;
      effectiveDate: string;
      enrollmentType: string;
      policyNumber: string;
      productCode: string;
      stateCode: string;
      transactionEffectiveDate: string;
    };
    premiumSplits: {
      premiumSplitParticipants: {
        producerLevel: number;
        producerSeries: string;
      }[];
    }[];
  };
}

export interface GpluAgeBandsAndSmokingFlags {
  ageBands: string[];
  smokingFlags: string[];
}
